import {
  CondensedButton,
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  List,
  Pill,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useState } from "react";
import Skeleton from "react-loading-skeleton";
import RichTextEditor from "react-rte";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import { ReflectionService } from "../../services/ReflectionService";
import { UserDetailContext } from "../../utils/Context";
import { fileWriter } from "../../utils/FileUtils";
import { formatToReadableTime } from "../../utils/utils";
import { ActionEditPanel } from "../Action/ActionEditPanel";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import { DialogYesNo } from "../DialogYesNo";
import ReflectionModal from "./ReflectionModal";
import { ReflectionVersionsToggle } from "./ReflectionVersionsToggle";
import {
  upsertReflection,
  upsertReflectionDraft,
  upsertReflectionDraftFromTemplate,
  upsertReflectionFromTemplate,
} from "./utils";

const ReflectionEntry = ({
  reflection,
  contextName,
  contextId,
  externalUserName,
  reflectionList,
  setReflectionList,
  actions,
  setActions,
  reflectionTemplates,
  sectionRefTemplate,
}) => {
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [openReflectionModal, setOpenReflectionModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [editingAction, setEditingAction] = useState(null);

  const actionDraft = {
    action_type: {},
    author: !externalUserName ? userDetails.name : externalUserName,
    reflections: [],
  };

  async function handleRemoveReflection() {
    try {
      const deleteReflection = externalUserName
        ? ExternalService.deleteExternalReflection
        : ReflectionService.deleteReflection;

      if (reflection.from_template) {
        const promiseList = reflection.sections.map((section) =>
          deleteReflection({
            contextId: contextId,
            reflectionId: reflection.ddl_id,
            sectionId: section.id,
            userId: reflection.is_draft ? userDetails.canvas_id : "",
          })
        );
        await Promise.all(promiseList);
      } else {
        await deleteReflection({
          contextId: contextId,
          reflectionId: reflection.ddl_id,
          userId: reflection.is_draft ? userDetails.canvas_id : "",
        });
      }

      setReflectionList((prevList) =>
        prevList.filter((item) => item.ddl_id !== reflection.ddl_id)
      );
      setOpenDeleteModal(false);
      showNotification(
        t("notifications.general.deleteSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  const handleUpdateReflection = async (reflectionText) => {
    try {
      const updatedReflection = await upsertReflection({
        reflectionText,
        author: externalUserName ? externalUserName : userDetails.name,
        contextId,
        reflectionId: reflection.ddl_id,
        authorId: reflection.is_draft ? userDetails.canvas_id : null,
        actions: reflection.actions,
        externalUserName: !!externalUserName,
      });
      setReflectionList((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  };

  async function handleUpdateReflectionFromTemplate(templateId, sections) {
    try {
      const updatedReflection = await upsertReflectionFromTemplate({
        sections,
        author: externalUserName ? externalUserName : userDetails.name,
        contextId,
        templateId,
        reflectionId: reflection.ddl_id,
        authorId: reflection.is_draft ? userDetails.canvas_id : null,
        actions: reflection.actions,
        externalUserName: !!externalUserName,
      });
      setReflectionList((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  const handleSaveReflectionDraft = async (
    reflectionText,
    existingReflection
  ) => {
    try {
      const updatedReflection = await upsertReflectionDraft({
        reflectionText,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId,
        reflectionId: existingReflection.ddl_id,
      });
      setReflectionList((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== existingReflection.ddl_id),
      ]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  };

  async function handleSaveReflectionDraftFromTemplate(templateId, sections) {
    try {
      const updatedReflection = await upsertReflectionDraftFromTemplate({
        sections,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId,
        templateId,
        reflectionId: reflection.ddl_id,
      });
      setReflectionList((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  async function handleRemoveAction() {
    try {
      const deleteAction = externalUserName
        ? ExternalService.deleteExternalAction
        : ActionService.deleteAction;

      await deleteAction(actionToDelete.id);
      const reflectionsToUpdate = reflectionList.filter((reflection) =>
        reflection.actions.includes(actionToDelete.id)
      );
      if (reflectionsToUpdate.length > 0) {
        for (const reflection of reflectionsToUpdate) {
          reflection.actions = reflection.actions.filter(
            (actionId) => actionId !== actionToDelete.id
          );
          await handleRemoveActionFromReflection(reflection);
        }
      }
      setActionToDelete(null);
      showNotification(
        t("notifications.general.deleteSuccess", { obj: t("actions.action") })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", { obj: t("actions.action") }),
        ERROR
      );
    }
  }

  const handleRemoveActionFromReflection = async (reflection) => {
    const updateReflection = externalUserName
      ? ExternalService.addExternalReflectionForContext
      : ReflectionService.addReflectionForContext;

    if (reflection.from_template) {
      const promiseList = reflection.sections.map((section) =>
        updateReflection({
          reflectionId: reflection.ddl_id,
          sectionId: section.id,
          templateId: reflection.template_id,
          contextId: contextId,
          author: reflection.metadata.author,
          actions: reflection.actions,
        })
      );
      const updatedReflections = await Promise.all(promiseList);

      updatedReflections.forEach((updatedReflection, index) => {
        const reflectionFile = fileWriter(
          reflection.sections[index].content,
          `${reflection.sections[index].id}-${index + 1}response.html`,
          "text/html"
        );
        Files.postPreSignedUrl(
          updatedReflection.url,
          updatedReflection.fields,
          reflectionFile
        );
      });

      setReflectionList((prevList) =>
        prevList.map((ref) =>
          ref.ddl_id === reflection.ddl_id ? reflection : ref
        )
      );
    } else {
      await updateReflection({
        reflectionId: reflection.ddl_id,
        contextId: contextId,
        author: reflection.metadata.author,
        actions: reflection.actions,
      }).then((newReflection) => {
        setReflectionList((prevList) =>
          prevList.map((item) =>
            item.ddl_id === reflection.ddl_id ? reflection : item
          )
        );

        const reflectionFile = fileWriter(
          reflection.content,
          reflection.name,
          "text/html"
        );
        Files.postPreSignedUrl(
          newReflection.url,
          newReflection.fields,
          reflectionFile
        );
      });
    }
  };

  const actionCreated = (newAction) => {
    setActions((prevMap) => [...prevMap, newAction]);
  };

  const actionUpdated = (updatedAction) => {
    setActions((prevMap) =>
      prevMap.map((action) => {
        if (action.id === updatedAction.id) {
          return updatedAction;
        }
        return action;
      })
    );
  };

  return (
    <Card
      style={{
        border: "1px solid #ccc",
        marginBottom: "10px",
        padding: "10px",
      }}
    >
      <Flex gap="small" wrap="wrap" alignItems="start">
        <Flex.Item shouldGrow>
          <Text as="p" size="small">
            {reflection.is_draft
              ? userDetails.name
              : reflection.metadata.author}
            <br />
            {formatToReadableTime(new Date(reflection.timestamp))}
          </Text>
        </Flex.Item>
        {reflection.is_draft && (
          <Flex.Item>
            <Pill color="success">{t("reflections.draft")}</Pill>
          </Flex.Item>
        )}
        <Flex.Item>
          <Tooltip color="primary-inverse" renderTip={t("reflections.editRef")}>
            <IconButton
              screenReaderLabel={t("reflections.editRef")}
              renderIcon={IconEditLine}
              size="small"
              onClick={() => setOpenReflectionModal(true)}
            />
          </Tooltip>
        </Flex.Item>
        <Flex.Item>
          <Tooltip
            color="primary-inverse"
            renderTip={t("reflections.deleteRef")}
          >
            <IconButton
              screenReaderLabel={t("reflections.deleteRef")}
              renderIcon={IconTrashLine}
              size="small"
              onClick={() => setOpenDeleteModal(true)}
            />
          </Tooltip>
        </Flex.Item>
        {reflection.from_template ? (
          <Flex.Item size="100%">
            <Flex wrap="wrap" gap="medium">
              {reflection.sections.map((section) => (
                <Flex.Item key={section.id} width="100%">
                  <Flex wrap="wrap" gap="small">
                    <Flex.Item width="100%">
                      <Text weight="bold">{section.header_question}</Text>
                    </Flex.Item>
                    {section.helper_text &&
                      section.helper_text !== EMPTY_RTE && (
                        <Flex.Item width="100%">
                          <RichTextEditor
                            className="readOnlyClass"
                            value={RichTextEditor.createValueFromString(
                              section.helper_text,
                              "html"
                            )}
                            readOnly
                          />
                        </Flex.Item>
                      )}
                    <Flex.Item padding="0 0 0 large">
                      <div
                        style={{
                          borderLeft: "2px solid #03893D",
                          paddingLeft: "0.75rem",
                        }}
                      >
                        <RichTextEditor
                          className="readOnlyClass"
                          value={RichTextEditor.createValueFromString(
                            section.content,
                            "html"
                          )}
                          readOnly
                        />
                      </div>
                    </Flex.Item>
                  </Flex>
                </Flex.Item>
              ))}
            </Flex>
          </Flex.Item>
        ) : (
          <Flex.Item size="100%">
            <RichTextEditor
              className="readOnlyClass"
              value={RichTextEditor.createValueFromString(
                reflection.content,
                "html"
              )}
              readOnly
            />
          </Flex.Item>
        )}

        {!reflection.is_draft && (
          <Flex.Item>
            <Text as="div" weight="bold">
              {t("actions.actions")}
              <CondensedButton
                size="small"
                renderIcon={IconAddLine}
                onClick={() => {
                  setEditingAction({ ...actionDraft });
                  setOpenActionModal(true);
                }}
                margin="0 0 0 small"
              >
                {t("actions.addAction")}
              </CondensedButton>
            </Text>

            {actions ? (
              reflection.actions.length > 0 ? (
                <List size="large" margin="0 0 small 0">
                  {reflection.actions.map((actionId, key) => {
                    const action = actions.find(
                      (action) => action.id === actionId
                    );
                    if (action) {
                      return (
                        <List.Item key={key}>
                          <Flex justifyItems="space-between">
                            <Flex.Item shouldShrink margin="0 small 0 0">
                              <Text as="p" size="small">
                                {action.text}
                              </Text>
                            </Flex.Item>
                            <Flex.Item align="start">
                              <Tooltip
                                color="primary-inverse"
                                renderTip={t("actions.editAction")}
                              >
                                <IconButton
                                  screenReaderLabel={t("actions.editAction")}
                                  renderIcon={IconEditLine}
                                  size="small"
                                  margin="0 small 0 0"
                                  onClick={() => {
                                    setEditingAction(action);
                                    setOpenActionModal(true);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip
                                color="primary-inverse"
                                renderTip={t("actions.deleteAction")}
                              >
                                <IconButton
                                  screenReaderLabel={t("actions.deleteAction")}
                                  renderIcon={IconTrashLine}
                                  size="small"
                                  onClick={() => {
                                    setActionToDelete(action);
                                  }}
                                />
                              </Tooltip>
                            </Flex.Item>
                          </Flex>
                        </List.Item>
                      );
                    }
                  })}
                </List>
              ) : (
                <Heading level="h5" as="h4">
                  {t("actions.noActions")}
                </Heading>
              )
            ) : (
              <Skeleton count={3} />
            )}
          </Flex.Item>
        )}
        {!externalUserName && (
          <Flex.Item size="100%">
            <ReflectionVersionsToggle
              contextId={contextId}
              reflection={reflection}
              currentReflectionIsDraft={reflection.is_draft}
              actions={actions}
            />
          </Flex.Item>
        )}
      </Flex>
      <ReflectionModal
        openModal={openReflectionModal}
        setOpenModal={setOpenReflectionModal}
        reflection={reflection}
        handleSubmit={handleUpdateReflection}
        handleSubmitFromTemplate={handleUpdateReflectionFromTemplate}
        onDraftChange={handleSaveReflectionDraft}
        onDraftChangeFromTemplate={handleSaveReflectionDraftFromTemplate}
        contextName={contextName}
        draftEnabled={!externalUserName}
        sectionRefTemplate={
          reflection.from_template ? sectionRefTemplate : null
        }
      />
      <ActionEditPanel
        title={editingAction ? t("actions.editAction") : t("actions.addAction")}
        contextId={contextId}
        action={editingAction}
        openModal={openActionModal}
        setOpenModal={setOpenActionModal}
        handleActionUpdated={actionUpdated}
        handleActionCreated={actionCreated}
        attachedReflections={[reflection]}
        external={externalUserName}
        handleClose={() => setEditingAction(null)}
        reflectionList={reflectionList}
        setReflectionList={setReflectionList}
        reflectionTemplates={reflectionTemplates}
      />
      <DialogYesNo
        message={t("reflections.deleteConfirm")}
        openStatus={openDeleteModal}
        onClickYes={handleRemoveReflection}
        onClickNo={() => setOpenDeleteModal(false)}
      />
      <DialogYesNo
        message={t("actions.deleteConfirm")}
        openStatus={!!actionToDelete}
        onClickYes={handleRemoveAction}
        onClickNo={() => setActionToDelete(null)}
      />
    </Card>
  );
};

export default ReflectionEntry;
